import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    AxisComponent,
    AxisData,
    AxisDataAxisPath,
    AxisOptions,
} from '@cmps/ng-chart';
import { TranslateService } from '@ngx-translate/core';
import { interpolateHclLong, quantize } from 'd3-interpolate';
import { timeSaturday } from 'd3-time';
import { Store } from 'redux';
import {
    animationFrameScheduler,
    fromEvent,
    Subject,
    Subscription,
} from 'rxjs';
import { auditTime, debounceTime, tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../common/date.pipe';
import { CmpsDecimalPipe } from '../common/number.pipe';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';
import { timeMonthEnd } from '../common/time-month-end';
import { ConfigViewPillar, Metric } from '../insight/config.insight.model';
import { GridInsightSelectionItem, RowId } from '../insight/grid.insight.model';
import {
    trendRetrieveData,
    trendRetrieveDataCancel,
} from '../insight/insight.action';
import { TrendState } from '../insight/insight.state';
import {
    OptionalColumn,
    OptionalColumnType,
    valueForColumn,
} from '../insight/optional-column.insight.model';
import { DataFilterOptions } from '../insight/performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    MetricData,
    NO_DATA_ID,
} from '../insight/performance-data/metric.pd.model';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';
import { PeriodType } from '../widget/date-range.widget.model';

import { TranslationKeyMap } from './performance-insight.i18n';

interface ChartDataAxisPath extends AxisDataAxisPath {
    color: string;
    rowId: string;
    colType: OptionalColumnType;
}

// TODO de-dup and merge into epics
@Component({
    selector: '[cmpsPerformanceInsightTrend]',
    templateUrl: './trend.pi.component.html',
})
@TakeUntilDestroy()
export class TrendPerformanceInsightComponent
    implements
        OnChanges,
        OnInit,
        AfterViewInit,
        OnDestroy,
        StateToProps<TrendState>
{
    @ViewChild('axis') private readonly _axis: AxisComponent;
    @Input() metrics: Metric[];
    @Input() eventDate: Date;
    @Input() initialDataFilterOpts: DataFilterOptions;
    @Input() columnSelection: OptionalColumn[];
    @Input() gridSelection: GridInsightSelectionItem[];
    @Input() metricSelection: Array<[ConfigViewPillar, Metric]>;
    @Input() className: string;
    @Input() gridClassName: string;
    i18n = TranslationKeyMap;
    private _storeSub: Subscription;
    state: TrendState;
    hasPendingData: boolean;
    chartOpts: AxisOptions;
    chartData: AxisData;
    private readonly _nextChartData: Subject<AxisData>;
    private _currentSelection: GridInsightSelectionItem[];

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef,
        private readonly _translateService: TranslateService,
        @Inject(STORE) private readonly _store: Store,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _decimalPipe: CmpsDecimalPipe,
    ) {
        this._nextChartData = new Subject();
        this._nextChartData
            .asObservable()
            .pipe(
                debounceTime(100),
                auditTime(0, animationFrameScheduler),
                // eslint-disable-next-line no-return-assign
                tap((nextChartData) => (this.chartData = nextChartData)),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) return;
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('gridSelection')) {
            this.propsToDispatch();
        }
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('eventDate') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('metricSelection')
        ) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._load(
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.gridSelection && this.gridSelection.map((e) => e.rowId),
                this.gridSelection,
            );
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('columnSelection')) {
            this._layoutData();
        }
    }

    ngOnInit(): void {
        this._storeSub = storeObserver<any, TrendState>(
            this._store,
            (state) => state.insight.trend,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        const prevState = this.state;
                        this.state = state;
                        this.stateToProps(prevState);
                    }),
                ),
            )
            .subscribe();

        const noData = this._translateService.instant(
            TranslationKeyMap._711_NO_DATA_AVAILABLE,
        );
        this.chartOpts = {
            rect: {
                width: '100%',
                height: '100%',
                top: 10,
                bottom: 10,
                left: 10,
            },
            strokeWidth: () => 2,
            strokeColor: (d: ChartDataAxisPath) => d.color,
            generateDataNoDataPaths: () => true,
            highlightOnMouseOver: true,
            showLegend: true,
            legend: {
                transitionDuration: 0,
            },
            label: (d) => {
                if (d.y == null) {
                    return noData;
                }
                const date = this._datePipe.transform(d.x, 'LLL dd y');
                const val = this._decimalPipe.transform(d.y, '1.2-2');
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `${date} - ${val}`;
            },
            transitionDuration: 0,
            yDomain: (yDomain) => {
                if (yDomain[0] === yDomain[1]) {
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    yDomain = [yDomain[0] - 10, yDomain[1] + 10];
                }
                return yDomain;
            },
            xAxis: (xAxis) => {
                switch (this.state.periodType) {
                    case PeriodType.DAY:
                    case PeriodType.WEEK:
                        xAxis.ticks(timeSaturday);
                        break;
                    case PeriodType.MONTH:
                        xAxis.ticks(timeMonthEnd);
                        break;
                    case PeriodType.QUARTER:
                        // eslint-disable-next-line no-case-declarations
                        const months = [2, 5, 8, 11];
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        xAxis.ticks(
                            timeMonthEnd.filter(
                                (date) =>
                                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                                    months.indexOf(date.getMonth()) !== -1,
                            ),
                        );
                        break;
                }
            },
            path: (d: ChartDataAxisPath, i, path, noDataPath) => {
                path.attr(
                    'stroke-dasharray',
                    noDataPath
                        ? '2,2'
                        : d.colType === OptionalColumnType.CURRENT_TARGET
                        ? '5,5'
                        : null,
                );
            },
        };
    }

    ngAfterViewInit(): void {
        const el = this._el.nativeElement.querySelector('.insight-trend');
        // TODO unsub
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        fromEvent(el, 'transitionend').subscribe(
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            () => this._axis && this._axis.render(),
        );
    }

    ngOnDestroy(): void {
        this._store.dispatch(trendRetrieveDataCancel());
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(prevState?: TrendState): void {
        if (!prevState) {
            this.propsToDispatch();
            return;
        }

        if (prevState.periodType !== this.state.periodType) {
            this.chartData = undefined;
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._load(
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._currentSelection &&
                    this._currentSelection.map((e) => e.rowId),
                this._currentSelection,
            );
        }

        if (prevState.pending.data !== this.state.pending.data) {
            this._layoutData();
        }

        this.hasPendingData =
            (!this.chartData || this.chartData.length === 0) &&
            Object.keys(this.state.pending.data).some(
                (k) => this.state.pending.data[k],
            );
    }

    propsToDispatch(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const toRemove =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._currentSelection &&
            this._currentSelection
                .filter(
                    (e) =>
                        !this.gridSelection ||
                        this.gridSelection.findIndex(
                            (ee) => ee.rowId === e.rowId,
                        ) === -1,
                )
                .reduce<string[]>((p, c) => p.concat(c.rowId), []);

        const toAdd: GridInsightSelectionItem[] = [];
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const next =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.gridSelection &&
            this.gridSelection.filter(
                (e) =>
                    !this._currentSelection ||
                    this._currentSelection.findIndex(
                        (ee) => ee.rowId === e.rowId,
                    ) === -1,
            );
        next && toAdd.push(...next);

        this._load(toRemove, toAdd);
    }

    private _load(toRemove: string[], toAdd: GridInsightSelectionItem[]): void {
        this._currentSelection = (this._currentSelection || [])
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            .filter((e) => toRemove.indexOf(e.rowId) === -1)
            .concat(toAdd);

        this._store.dispatch(trendRetrieveDataCancel(toRemove));

        if (
            this.state.periodType == null ||
            toAdd.length === 0 ||
            !this.metricSelection ||
            this.metricSelection.length === 0
        ) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const period =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state.startDates &&
            this.state.startDates.find(
                (e) => e.periodType === this.state.periodType,
            );
        if (!period) return;

        const exclude = [
            CriteriaIdType.USER_ROLE_ID,
            CriteriaIdType.TEAM_ROLE_ID,
        ];
        toAdd.forEach((e) => {
            const levels = e.levelsDataMapRow.filter(
                (ee) => !exclude.includes(ee.outputCriteriaType),
            );
            const level = levels && levels.length !== 0 && levels[0]; // TODO handle null level
            this._store.dispatch(
                trendRetrieveData(
                    {
                        identifier: level.identifier,
                        viewType: level.performanceDataViewType,
                        displayNameCriteriaIdType:
                            level.displayNameCriteriaIdType,
                        outputCriteriaIdType: level.outputCriteriaType,
                        inputCriteriaIdTypes: null,
                        periodType: this.state.periodType,
                        date: this.eventDate,
                        startDate: period.startDate,
                        lobIdList: [
                            ...(this.initialDataFilterOpts.lobIdList || []),
                        ],
                        regionIdList: [
                            ...(this.initialDataFilterOpts.regionIdList || []),
                        ],
                        areaIdList: [
                            ...(this.initialDataFilterOpts.areaIdList || []),
                        ],
                        siteIdList: [
                            ...(this.initialDataFilterOpts.siteIdList || []),
                        ],
                        peerGroupIdList: [
                            ...(this.initialDataFilterOpts.peerGroupIdList ||
                                []),
                        ],
                        superGroupIdList: [
                            ...(this.initialDataFilterOpts.superGroupIdList ||
                                []),
                        ],
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        metricIdList:
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            this.metricSelection &&
                            this.metricSelection.map((ee) => ee[1].metricId),
                    },
                    e.rowId,
                    e.levelsDataMap
                        .concat(level)
                        .filter((ee) => ee.identifier === level.identifier),
                ),
            );
        });
    }

    private _layoutData(): void {
        if (!this._currentSelection || !this.state.data) return;

        const selection = this._currentSelection.filter(
            (e) => this.state.pending.data[e.rowId] === false,
        );
        const data = selection
            .map((e) => {
                const rid = RowId.parse(e.rowId);
                const dat = this.state.data.find((ee) => {
                    return (
                        ee.levelId === rid.levelIdentifier &&
                        ee.levelIdPath === rid.levelIdentifierPath &&
                        ee.data.some((eee) => eee.dataId === rid.dataId)
                    );
                });
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                const peerGroupDat =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this.state.peerGroupData &&
                    this.state.peerGroupData.find((ee) => {
                        return (
                            ee.levelId === rid.levelIdentifier &&
                            ee.levelIdPath === rid.levelIdentifierPath &&
                            ee.data.some((eee) => eee.dataId === rid.dataId)
                        );
                    });
                return {
                    rowId: e.rowId,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    name:
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        (dat && dat.data && dat.data.length !== 0
                            ? dat.data[0].name
                            : null) || e.name,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    data: dat && dat.data,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    peerGroupData: peerGroupDat && peerGroupDat.data,
                };
            })
            .filter((e) => e.data);
        this._formatData(data);
    }

    private _formatData(
        data: Array<{
            rowId: string;
            name: string;
            data: MetricData[];
            peerGroupData: MetricData[];
        }>,
    ): void {
        const noData = this._translateService.instant(
            TranslationKeyMap._711_NO_DATA_AVAILABLE,
        );

        const group1Columns = [
            OptionalColumnType.CURRENT_ACTUAL,
            OptionalColumnType.PREVIOUS_ACTUAL,
            OptionalColumnType.CURRENT_TARGET,
            OptionalColumnType.GOAL,
            OptionalColumnType.PEER_GROUP_ACTUAL,
        ];
        const group2Columns = [
            OptionalColumnType.PROJECTED_PERCENT_TO_TARGET,
            OptionalColumnType.PERCENT_TO_TARGET,
            OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET,
        ];
        const group3Columns = [OptionalColumnType.GAP];
        const group4Columns = [
            OptionalColumnType.PROJECTED_RESULT,
            OptionalColumnType.PERIOD_TARGET,
        ];
        const groups = [
            group1Columns,
            group2Columns,
            group3Columns,
            group4Columns,
        ];
        const axises = this.metricSelection.reduce<
            Array<{ metric: Metric; columns: OptionalColumn[] }>
        >((p, c) => {
            if (c[1]) {
                groups.forEach((e) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    const group = this.columnSelection.filter(
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        (ee) => e.indexOf(ee.id) !== -1,
                    );
                    group.length !== 0 &&
                        p.push({ metric: c[1], columns: group });
                });
            }
            return p;
        }, []);

        const peerGroupColumns = [
            OptionalColumnType.PEER_GROUP_ACTUAL,
            OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET,
        ];
        const percentColumns = [
            OptionalColumnType.PROJECTED_PERCENT_TO_TARGET,
            OptionalColumnType.PERCENT_TO_TARGET,
            OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET,
        ];

        const numLines =
            this.columnSelection.length *
            this.metricSelection.length *
            data.length;
        const colors = quantize(interpolateHclLong('red', 'blue'), numLines);

        let colorIndex = 0;
        const nextChartData = axises
            .map((axis, iaxis) => {
                const paths = data.reduce((p, row) => {
                    let actualOrTargetColorSeen = false;
                    let actualAndTargetColorIndex = null;
                    const colPaths = axis.columns.map((column) => {
                        const dat =
                            /* eslint-disable @typescript-eslint/prefer-includes */
                            (
                                peerGroupColumns.indexOf(column.id) === -1
                                    ? row.data
                                    : row.peerGroupData
                            )[0];
                        /* eslint-enable @typescript-eslint/prefer-includes */
                        const pathData = dat.metrics.filter(
                            (e) => e.id === axis.metric.metricId,
                        );
                        let key = `${axis.metric.shortName} - ${column.name} - ${row.name}`;
                        if (dat.id === NO_DATA_ID) {
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            key = `${key} (${noData})`;
                        }
                        const points = pathData
                            .map((e) => ({
                                x: e.eventDate,
                                y: valueForColumn(column.id, e),
                            }))
                            .filter((e) => e.x && e.y != null)
                            .sort((a, b) => a.x.getTime() - b.x.getTime());
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        if (percentColumns.indexOf(column.id) !== -1) {
                            // eslint-disable-next-line no-return-assign
                            points.forEach((e) => (e.y *= 100));
                        }
                        // CURRENT_TARGET will be the same as CURRENT_ACTUAL
                        let nextColorIndex = colorIndex;
                        if (
                            column.id === OptionalColumnType.CURRENT_ACTUAL ||
                            column.id === OptionalColumnType.CURRENT_TARGET
                        ) {
                            if (!actualOrTargetColorSeen) {
                                actualAndTargetColorIndex = colorIndex;
                                actualOrTargetColorSeen = true;
                            }
                            nextColorIndex = actualAndTargetColorIndex;
                        }
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        const ret = {
                            key,
                            points,
                            color: colors[
                                Math.min(colors.length - 1, nextColorIndex)
                            ], // TODO revise
                            rowId: row.rowId,
                            colType: column.id,
                        } as ChartDataAxisPath;
                        colorIndex++;
                        return ret;
                    });
                    return p.concat(colPaths);
                }, []);
                return {
                    id: `${axis.metric.metricId}-${iaxis}`,
                    paths,
                };
            })
            .filter((e) => e.paths.length !== 0);
        this._nextChartData.next(nextChartData);
    }
}
